import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { CustomerOrderInvoice } from '../../customerOrdersApi'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import { EmptyStateBlock, SkeletonBlock, customerMotionTransition } from '../customer-area'
import { CustomerOrderInvoiceItem } from './CustomerOrderInvoiceItem'
import { useInvoicesSkeletonVisibility } from './useInvoicesSkeletonVisibility'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

type CustomerOrderInvoicesSectionProps = {
  title: string
  loadingLabel: string
  emptyLabel: string
  openInvoiceHintLabel: string
  openingInvoiceHintLabel: string
  invoicesLoading: boolean
  invoicesError: string
  invoiceOpenError: string
  orderInvoices: CustomerOrderInvoice[]
  invoiceOpeningId: string | null
  locale: ShopLocale
  t: TranslateFn
  onOpenInvoice: (invoice: CustomerOrderInvoice) => void
}

export const CustomerOrderInvoicesSection = ({
  title,
  loadingLabel,
  emptyLabel,
  openInvoiceHintLabel,
  openingInvoiceHintLabel,
  invoicesLoading,
  invoicesError,
  invoiceOpenError,
  orderInvoices,
  invoiceOpeningId,
  locale,
  t,
  onOpenInvoice,
}: CustomerOrderInvoicesSectionProps) => {
  const reducedMotion = useReducedMotion()
  const invoicesSkeletonVisible = useInvoicesSkeletonVisibility({
    invoicesLoading,
    minVisibleMs: 760,
  })
  const subtitleLabel =
    orderInvoices.length === 1
      ? t('customer.orderDetail.invoicesSubtitleSingle')
      : orderInvoices.length > 1
        ? t('customer.orderDetail.invoicesSubtitleMany', { count: orderInvoices.length })
        : t('customer.orderDetail.invoicesSubtitle')
  const skeletonTransition = reducedMotion ? { duration: 0 } : { duration: 0.16, ease: customerMotionTransition.ease }
  const contentTransition = reducedMotion ? { duration: 0 } : { duration: 0.24, ease: customerMotionTransition.ease }

  return (
    <section className="ca-card-shell ca-theme-transition rounded-xl px-4 py-4">
      <header className="border-b border-(--color-border-default) pb-2.5">
        <h3 className="text-sm font-semibold text-(--color-text-primary)">{title}</h3>
        <p className="mt-0.5 text-xs text-(--color-text-secondary)">{subtitleLabel}</p>
      </header>

      {!invoicesSkeletonVisible && !invoicesLoading && !invoicesError && invoiceOpenError ? (
        <p className="mt-3 rounded-lg border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-sm text-(--color-status-error)">
          {invoiceOpenError}
        </p>
      ) : null}

      <AnimatePresence mode="wait" initial={false}>
        {invoicesSkeletonVisible ? (
          <motion.div
            key="invoices-loading"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={skeletonTransition}
            className="mt-3 space-y-3"
          >
            <p className="text-sm text-(--color-text-secondary)">{loadingLabel}</p>
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={`invoice-loading-${index}`} className="ca-surface-muted ca-theme-transition rounded-xl px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <SkeletonBlock className="h-10 w-10 rounded-lg" />
                    <div className="space-y-2">
                      <SkeletonBlock className="h-4 w-32 rounded-md" />
                      <SkeletonBlock className="h-3 w-36 rounded-md" />
                    </div>
                  </div>
                  <SkeletonBlock className="h-9 w-24 rounded-md" />
                </div>
              </div>
            ))}
          </motion.div>
        ) : null}

        {!invoicesSkeletonVisible && !invoicesLoading && invoicesError ? (
          <motion.p
            key="invoices-error"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={contentTransition}
            className="mt-3 rounded-lg border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-sm text-(--color-status-error)"
          >
            {invoicesError}
          </motion.p>
        ) : null}

        {!invoicesSkeletonVisible && !invoicesLoading && !invoicesError && orderInvoices.length === 0 ? (
          <motion.div
            key="invoices-empty"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={contentTransition}
            className="mt-3"
          >
            <EmptyStateBlock
              title={emptyLabel}
              description={t('customer.orderDetail.invoicesEmptyHint')}
              icon={
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8}>
                  <path d="M7 3.5h7l4 4v13h-11a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
                  <path d="M14 3.5v4h4M8.5 12h7M8.5 15.5h7" />
                </svg>
              }
            />
          </motion.div>
        ) : null}

        {!invoicesSkeletonVisible && !invoicesLoading && !invoicesError && orderInvoices.length > 0 ? (
          <motion.ul
            key="invoices-list"
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6 }}
            animate={reducedMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
            transition={contentTransition}
            className="mt-3 space-y-3"
          >
            {orderInvoices.map((invoice, index) => (
              <CustomerOrderInvoiceItem
                key={invoice.id}
                index={index}
                invoice={invoice}
                invoiceOpeningId={invoiceOpeningId}
                openInvoiceHintLabel={openInvoiceHintLabel}
                openingInvoiceHintLabel={openingInvoiceHintLabel}
                reducedMotion={Boolean(reducedMotion)}
                locale={locale}
                t={t}
                onOpenInvoice={onOpenInvoice}
              />
            ))}
          </motion.ul>
        ) : null}
      </AnimatePresence>
    </section>
  )
}
